import { create } from 'zustand';
import { supplierProductDetailApi } from '@api/api-config.ts';
import {
  SupplierProductDetailResponse,
  SupplierProductImageResponse,
  SupplierProductCustomerResponse,
  SupplierProductAuctionResponse,
  SupplierProductPortalResponse,
  SupplierProductPriceResponse,
  PriceType,
  GetSupplierProductDetailPricesGuaranteedFilterEnum,
  ApprovalStatus,
} from '@api/generated/search-service';

interface PaginationState {
  page: number;
  size: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface SupplierProductDetailStoreState {
  // Základní detail
  loading: boolean;
  error: string | null;
  product: SupplierProductDetailResponse | null;

  // Obrázky
  images: SupplierProductImageResponse[];
  imagesLoading: boolean;
  imageUploading: boolean;
  imageDeleting: number | null; // physicalImageId being deleted

  // Zákazníci
  customers: SupplierProductCustomerResponse[];
  customersLoading: boolean;

  // Aukce
  auctions: SupplierProductAuctionResponse[];
  auctionsLoading: boolean;

  // Portály
  portals: SupplierProductPortalResponse[];
  portalsLoading: boolean;

  // Ceny (stránkované)
  prices: SupplierProductPriceResponse[];
  pricesLoading: boolean;
  pricesPagination: PaginationState | null;

  // Akce
  loadProductDetail: (supplierId: number, portalId: number, productId: number) => Promise<void>;
  loadImages: (supplierId: number, portalId: number, productId: number) => Promise<void>;
  uploadImage: (supplierId: number, portalId: number, productId: number, data: { base64Data: string; fileName: string; isPrimary: boolean }) => Promise<boolean>;
  deleteImage: (supplierId: number, portalId: number, productId: number, physicalImageId: number) => Promise<boolean>;
  loadCustomers: (supplierId: number, portalId: number, productId: number, query?: string) => Promise<void>;
  loadAuctions: (supplierId: number, portalId: number, productId: number, query?: string) => Promise<void>;
  loadPortals: (supplierId: number, portalId: number, productId: number) => Promise<void>;
  loadPrices: (
    supplierId: number,
    portalId: number,
    productId: number,
    params: {
      page?: number;
      size?: number;
      organizationId?: number;
      auctionId?: number;
      guaranteedFilter?: GetSupplierProductDetailPricesGuaranteedFilterEnum;
      priceType?: PriceType;
    },
  ) => Promise<void>;
  reset: () => void;
}

export const useSupplierProductDetailStore = create<SupplierProductDetailStoreState>(set => ({
  // Základní detail
  loading: false,
  error: null,
  product: null,

  // Obrázky
  images: [],
  imagesLoading: false,
  imageUploading: false,
  imageDeleting: null,

  // Zákazníci
  customers: [],
  customersLoading: false,

  // Aukce
  auctions: [],
  auctionsLoading: false,

  // Portály
  portals: [],
  portalsLoading: false,

  // Ceny
  prices: [],
  pricesLoading: false,
  pricesPagination: null,

  loadProductDetail: async (supplierId: number, portalId: number, productId: number) => {
    try {
      set({ loading: true, error: null });
      const response = await supplierProductDetailApi.getSupplierProductDetail({ supplierId, portalId, productId });
      set({
        loading: false,
        error: null,
        product: response.data,
      });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Chyba při načítání detailu produktu',
        product: null,
      });
    }
  },

  loadImages: async (supplierId: number, portalId: number, productId: number) => {
    try {
      set({ imagesLoading: true });
      const response = await supplierProductDetailApi.getSupplierProductDetailImages({ supplierId, portalId, productId });
      set({
        imagesLoading: false,
        images: response.data,
      });
    } catch (error) {
      set({
        imagesLoading: false,
        images: [],
      });
    }
  },

  uploadImage: async (supplierId, portalId, productId, data) => {
    try {
      set({ imageUploading: true });
      const response = await supplierProductDetailApi.uploadSupplierProductDetailImage({
        supplierId,
        portalId,
        productId,
        supplierProductImageUploadRequest: data,
      });
      // Po úspěšném uploadu přidáme obrázek do seznamu
      const newImage: SupplierProductImageResponse = {
        physicalImageId: response.data.physicalImageId,
        s3Key: response.data.s3Key,
        isPrimary: data.isPrimary,
        isSupplierImage: true,
        approvalStatus: ApprovalStatus.Pending,
      };
      set(state => ({
        imageUploading: false,
        images: [...state.images, newImage],
      }));
      return true;
    } catch (error) {
      set({ imageUploading: false });
      return false;
    }
  },

  deleteImage: async (supplierId, portalId, productId, physicalImageId) => {
    try {
      set({ imageDeleting: physicalImageId });
      await supplierProductDetailApi.deleteSupplierProductDetailImage({
        supplierId,
        portalId,
        productId,
        physicalImageId,
      });
      // Po úspěšném smazání odebereme obrázek ze seznamu
      set(state => ({
        imageDeleting: null,
        images: state.images.filter(img => img.physicalImageId !== physicalImageId),
      }));
      return true;
    } catch (error) {
      set({ imageDeleting: null });
      return false;
    }
  },

  loadCustomers: async (supplierId: number, portalId: number, productId: number, query?: string) => {
    try {
      set({ customersLoading: true });
      const response = await supplierProductDetailApi.getSupplierProductDetailCustomers({
        supplierId,
        portalId,
        productId,
        query,
      });
      set({
        customersLoading: false,
        customers: response.data,
      });
    } catch (error) {
      set({
        customersLoading: false,
        customers: [],
      });
    }
  },

  loadAuctions: async (supplierId: number, portalId: number, productId: number, query?: string) => {
    try {
      set({ auctionsLoading: true });
      const response = await supplierProductDetailApi.getSupplierProductDetailAuctions({
        supplierId,
        portalId,
        productId,
        query,
      });
      set({
        auctionsLoading: false,
        auctions: response.data,
      });
    } catch (error) {
      set({
        auctionsLoading: false,
        auctions: [],
      });
    }
  },

  loadPortals: async (supplierId: number, portalId: number, productId: number) => {
    try {
      set({ portalsLoading: true });
      const response = await supplierProductDetailApi.getSupplierProductDetailPortals({ supplierId, portalId, productId });
      set({
        portalsLoading: false,
        portals: response.data,
      });
    } catch (error) {
      set({
        portalsLoading: false,
        portals: [],
      });
    }
  },

  loadPrices: async (supplierId: number, portalId: number, productId: number, params) => {
    try {
      set({ pricesLoading: true });
      const response = await supplierProductDetailApi.getSupplierProductDetailPrices({
        supplierId,
        portalId,
        productId,
        page: params.page || 1,
        size: params.size || 20,
        organizationId: params.organizationId,
        auctionId: params.auctionId,
        guaranteedFilter: params.guaranteedFilter,
        priceType: params.priceType,
      });
      set({
        pricesLoading: false,
        prices: response.data.items,
        pricesPagination: {
          page: response.data.page,
          size: response.data.size,
          total: response.data.total,
          totalPages: response.data.totalPages,
          hasNext: response.data.hasNext,
          hasPrev: response.data.hasPrev,
        },
      });
    } catch (error) {
      set({
        pricesLoading: false,
        prices: [],
        pricesPagination: null,
      });
    }
  },

  reset: () => {
    set({
      loading: false,
      error: null,
      product: null,
      images: [],
      imagesLoading: false,
      imageUploading: false,
      imageDeleting: null,
      customers: [],
      customersLoading: false,
      auctions: [],
      auctionsLoading: false,
      portals: [],
      portalsLoading: false,
      prices: [],
      pricesLoading: false,
      pricesPagination: null,
    });
  },
}));
